// src/block/index.ts
import { Type as Type11 } from "@sinclair/typebox";

// src/block/finish.ts
import { Type as Type2 } from "@sinclair/typebox";

// src/block/base.ts
import { Type } from "@sinclair/typebox";
var Base = Type.Object({
  kind: Type.Literal("block"),
  uid: Type.String(),
  sid: Type.String(),
  version: Type.Number(),
  state: Type.Object({
    active: Type.Boolean(),
    entered: Type.Boolean(),
    exited: Type.Boolean()
  })
});
var Empty = Type.Object({
  ...Base.properties,
  transitioned: Type.Object({})
});
var Unary = Type.Object({
  ...Base.properties,
  transitioned: Type.Object({
    default: Type.Boolean()
  })
});
var OptionallyUnary = Type.Object({
  ...Base.properties,
  transitioned: Type.Union([
    Type.Object({
      default: Type.Boolean()
    }),
    Type.Object({})
  ])
});
var Binary = Type.Object({
  ...Base.properties,
  transitioned: Type.Object({
    true: Type.Boolean(),
    false: Type.Boolean()
  })
});
var Nary = Type.Object({
  ...Base.properties,
  transitioned: Type.Record(Type.String(), Type.Boolean())
});

// src/block/finish.ts
var Finish = Type2.Object(
  {
    ...Empty.properties,
    type: Type2.Literal("finish")
  },
  { $id: "Finish" }
);

// src/block/entry.ts
import { Type as Type3 } from "@sinclair/typebox";
var Entry = Type3.Object(
  {
    ...Unary.properties,
    type: Type3.Literal("entry"),
    expression: Type3.Function([], Type3.Promise(Type3.Boolean()))
  },
  { $id: "Entry" }
);

// src/block/gate.ts
import { Type as Type4 } from "@sinclair/typebox";
var Gate = Type4.Object(
  {
    ...Nary.properties,
    type: Type4.Literal("gate")
  },
  { $id: "Gate" }
);

// src/block/boolean.ts
import { Type as Type5 } from "@sinclair/typebox";
var Boolean = Type5.Object(
  {
    ...Binary.properties,
    type: Type5.Literal("boolean"),
    expression: Type5.Function([], Type5.Promise(Type5.Boolean()))
  },
  { $id: "Boolean" }
);

// src/block/model.ts
import { Type as Type7 } from "@sinclair/typebox";

// src/fields/fields.ts
import { Type as Type6 } from "@sinclair/typebox";
var FIELD_VALUE_LITERALS = {
  string: "string",
  boolean: "boolean",
  number: "number"
};
var BaseField = Type6.Object(
  {
    sid: Type6.Readonly(Type6.String())
  },
  { $id: "BaseField" }
);
var StringField = Type6.Intersect(
  [
    BaseField,
    Type6.Object({
      type: Type6.Readonly(Type6.Literal(FIELD_VALUE_LITERALS.string)),
      value: Type6.Readonly(Type6.Union([Type6.String(), Type6.Null()]))
    })
  ],
  { $id: "StringField" }
);
var NumberField = Type6.Intersect(
  [
    BaseField,
    Type6.Object({
      type: Type6.Readonly(Type6.Literal(FIELD_VALUE_LITERALS.number)),
      value: Type6.Readonly(Type6.Union([Type6.Number(), Type6.Null()]))
    })
  ],
  { $id: "NumberField" }
);
var BooleanField = Type6.Intersect(
  [
    BaseField,
    Type6.Object({
      type: Type6.Readonly(Type6.Literal(FIELD_VALUE_LITERALS.boolean)),
      value: Type6.Readonly(Type6.Union([Type6.Boolean(), Type6.Null()]))
    })
  ],
  { $id: "BooleanField" }
);
var Field = Type6.Union([StringField, NumberField, BooleanField], {
  $id: "Field"
});
var Fields = Type6.Array(Field);

// src/fields/helpers.ts
var fieldToString = (field) => {
  return field == null ? null : JSON.stringify(field);
};
var fieldFromString = (string, type) => {
  if (typeof string !== "string" || string === "null") {
    return null;
  }
  try {
    const value = JSON.parse(string);
    switch (type) {
      case "number":
        return value;
      case "boolean":
        return value;
      case "string":
        return value;
    }
  } catch (e) {
    throw new Error(`value: ${string} cannot be coerced into ${type}`);
  }
};
var castField = ({
  sid,
  type,
  value
}) => {
  switch (type) {
    case "boolean":
      return {
        sid,
        type: "boolean",
        value
      };
    case "number":
      return {
        sid,
        type: "number",
        value
      };
    case "string":
      return {
        sid,
        type: "string",
        value
      };
    default:
      throw new Error(
        `Attempting to cast field with type ${type} which is not supported`
      );
  }
};
function isValueOfType(value, type) {
  if (value == null || value === "null") {
    return true;
  }
  if (typeof value === type) {
    return true;
  }
  return false;
}
function isFieldTypeLiteral(str) {
  return FIELD_VALUE_LITERALS[str] !== void 0;
}

// src/block/model.ts
var Model = Type7.Object(
  {
    ...Nary.properties,
    type: Type7.Literal("model"),
    fields: Fields
  },
  { $id: "Model" }
);

// src/block/set.ts
import { Type as Type8 } from "@sinclair/typebox";
var Set = Type8.Object(
  {
    ...Unary.properties,
    size: Type8.Number(),
    blocks: Type8.Array(Element),
    type: Type8.Literal("set"),
    ordered: Type8.Optional(Type8.Boolean())
  },
  { $id: "Set" }
);

// src/block/element.ts
import { Type as Type9 } from "@sinclair/typebox";
var Element = Type9.Object(
  {
    ...OptionallyUnary.properties,
    type: Type9.Literal("element"),
    fields: Fields
  },
  { $id: "Element" }
);

// src/block/webhook.ts
import { Type as Type10 } from "@sinclair/typebox";
var Webhook = Type10.Object(
  {
    ...Unary.properties,
    type: Type10.Literal("webhook"),
    request: Type10.Function(
      [],
      Type10.Promise(
        Type10.Object({
          ok: Type10.Boolean(),
          redirected: Type10.Boolean(),
          status: Type10.Number(),
          statusText: Type10.String(),
          url: Type10.String()
        })
      )
    )
  },
  { $id: "Webhook" }
);

// src/block/index.ts
var BLOCK_TYPES = {
  entry: Entry.properties.type.const,
  boolean: Boolean.properties.type.const,
  model: Model.properties.type.const,
  finish: Finish.properties.type.const,
  set: Set.properties.type.const,
  element: Element.properties.type.const,
  webhook: Webhook.properties.type.const,
  gate: Gate.properties.type.const
};
var BlockTypes = Type11.Union([
  Entry.properties.type,
  Boolean.properties.type,
  Model.properties.type,
  Finish.properties.type,
  Set.properties.type,
  Element.properties.type,
  Webhook.properties.type,
  Gate.properties.type
]);
var Block = Type11.Union(
  [
    Type11.Ref(Model),
    Type11.Ref(Entry),
    Type11.Ref(Boolean),
    Type11.Ref(Finish),
    Type11.Ref(Set),
    Type11.Ref(Element),
    Type11.Ref(Webhook),
    Type11.Ref(Gate)
  ],
  { $id: "Block" }
);
var Blocks = Type11.Array(Type11.Ref(Block));
var SET_ELEMENTS = [Model.properties.type.const];
function getDefaultTransition(props) {
  const defaultTransition = Object.entries(props.transitioned).filter(
    ([name]) => name === "default"
  );
  const defaultTransitionValue = {
    default: false
  };
  if (defaultTransition.length) {
    defaultTransitionValue.default = defaultTransition[0][1];
  }
  return defaultTransitionValue;
}
function getDefaultBlock(props) {
  const defaultState = {
    active: false,
    entered: false,
    exited: false,
    ...props.state
  };
  const { transitioned } = { transitioned: props.transitioned || {} };
  switch (props.type) {
    case "model":
    case "element":
      return {
        kind: "block",
        fields: [],
        transitioned: getDefaultTransition({ transitioned }),
        state: defaultState,
        ...props,
        type: "model"
      };
    case "set":
      return {
        ...props,
        type: "set",
        kind: "block",
        size: 0,
        blocks: [],
        state: defaultState,
        transitioned: getDefaultTransition({ transitioned })
      };
    case "webhook":
      return {
        ...props,
        type: "webhook",
        kind: "block",
        state: defaultState,
        transitioned: getDefaultTransition({ transitioned }),
        request: async () => ({
          ok: true,
          redirected: false,
          status: 204,
          statusText: "",
          url: ""
        })
      };
    case "entry":
      return {
        ...props,
        type: "entry",
        kind: "block",
        state: defaultState,
        transitioned: getDefaultTransition({ transitioned }),
        expression: async () => false
      };
    case "finish":
      return {
        ...props,
        state: defaultState,
        transitioned: getDefaultTransition({ transitioned }),
        type: "finish",
        kind: "block"
      };
    case "gate":
      return {
        kind: "block",
        transitioned: getDefaultTransition({ transitioned }),
        state: defaultState,
        ...props,
        type: "gate"
      };
    case "boolean":
      return {
        ...props,
        type: "boolean",
        kind: "block",
        state: defaultState,
        transitioned: {
          ...{ true: false, false: false },
          ...transitioned
        },
        expression: async () => false
      };
    default:
      throw new Error(`Factory not implemented for ${props.type}`);
  }
}

// src/intent/index.ts
import { Type as Type12 } from "@sinclair/typebox";
var FlowIntent = Type12.Union(
  [
    Type12.Literal("finish", {
      $id: "finish_intent_flow",
      title: "Finish_Intent"
    }),
    Type12.Literal("stop", { $id: "stop_intent", title: "Stop_Intent" }),
    Type12.Literal("reset", { $id: "reset_intent", title: "Reset_Intent" }),
    Type12.Literal("start", { $id: "start_intent", title: "Start_Intent" })
  ],
  { $id: "FlowIntent", title: "Flow_Intent" }
);

// src/flow/index.ts
import { Type as Type13 } from "@sinclair/typebox";
var FlowState = Type13.Readonly(
  Type13.Object(
    {
      stopped: Type13.Boolean(),
      started: Type13.Boolean(),
      finished: Type13.Boolean()
    },
    { $id: "FlowState" }
  )
);
var Flow = Type13.Object(
  {
    kind: Type13.Readonly(Type13.Literal("flow")),
    type: Type13.Readonly(Type13.Literal("flow")),
    uid: Type13.Readonly(Type13.String()),
    sid: Type13.Readonly(Type13.String()),
    version: Type13.Readonly(Type13.Number()),
    state: FlowState
  },
  { $id: "Flow" }
);

// src/edge/index.ts
import { Type as Type14 } from "@sinclair/typebox";
var Edge = Type14.Object(
  {
    kind: Type14.Literal("edge"),
    type: Type14.Literal("edge"),
    value: Type14.Tuple([Block, Block]),
    input: Type14.String()
  },
  { $id: "Edge" }
);
export {
  BLOCK_TYPES,
  Block,
  BlockTypes,
  Blocks,
  Boolean,
  BooleanField,
  Edge,
  Element,
  Entry,
  FIELD_VALUE_LITERALS,
  Field,
  Fields,
  Finish,
  Flow,
  FlowIntent,
  FlowState,
  Gate,
  Model,
  NumberField,
  SET_ELEMENTS,
  Set,
  StringField,
  Webhook,
  castField,
  fieldFromString,
  fieldToString,
  getDefaultBlock,
  isFieldTypeLiteral,
  isValueOfType
};
